import clsx from "clsx";
import React, {
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import { useIntl } from "react-intl";
import { Row, useRowSelect, useTable } from "react-table";
import {Form} from "react-bootstrap";
import {CustomColumn} from "./tableCustomeModel";
import { SELECTION_MODE } from "../../../utils/Constant";
import { KTCardBody } from "../../../../../_metronic/helpers";
import { TableCustomToolbar } from "../components/TableToolbar";
import { TableCustomHeaderColumn } from "../components/TableCustomHeaderColumn";
import "../table.scss";

interface ITableProps<T extends object> extends React.HTMLAttributes<HTMLDivElement> {
  data: T[];
  isClearSelect?: boolean;
  columnsActions?: any;
  columns: ReadonlyArray<CustomColumn<T>>;
  selectionMode?: typeof SELECTION_MODE[keyof typeof SELECTION_MODE];
  hasToolbar?: boolean;
  handleSearchByPage?: () => void;
  handleChangeValueInput?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleOpenInfoDialog?: (row: any) => void;
  getSelectedRowsData?: Dispatch<SetStateAction<any>> | any;
  height?: number | string;
  pageIndex?: number;
  pageSize?: number;
  minHeight?: number | string;
  maxHeight?: number | string;
  selectedLabel?: boolean;
  isHiddenCheckall?: boolean;
  verticalScroll?: boolean;
  handleDoubleClick?: (row: any) => void;
  setShouldOpenFilterSearch?: any;
  shouldOpenFilterSearch?: boolean;
  className?: string;
  rowSelected?: any;
  handleConditionSelectRows?: any;
  isCondition?: boolean;
  handleContextMenu?: (e: any, row: any) => void;
  onScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
  showThumbnail?: boolean;
}

function TableCustom<T extends object>(props: Readonly<ITableProps<T>>) {
  const {
    data,
    isHiddenCheckall = false,
    isClearSelect,
    columns,
    columnsActions,
    selectionMode,
    hasToolbar,
    getSelectedRowsData,
    height,
    minHeight,
    maxHeight,
    selectedLabel,
    verticalScroll,
    handleDoubleClick,
    setShouldOpenFilterSearch,
    shouldOpenFilterSearch,
    className,
    handleConditionSelectRows,
    isCondition,
    handleContextMenu,
    onScroll,
  } = props;
  const [selectedRows, setSelectedRows] = useState<Row<T>[]>([]);
  const [styles, setStyles] = useState<object>({});
  const intl = useIntl();
  useEffect(() => {
    isClearSelect && setSelectedRows([])
  }, [isClearSelect])
  useEffect(() => {
    setStyles({
      ...styles,
      minHeight: minHeight,
      maxHeight: maxHeight,
    });
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minHeight, maxHeight]);

  useEffect(() => {
    if (getSelectedRowsData) {
      getSelectedRowsData(selectedRows?.map((item) => item?.original));
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRows]);

  useEffect(() => {
    handleConditionSelectRows?.(rows, setSelectedRows);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCondition]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    allColumns,
    rows,
  } = useTable(
    {
      columns,
      data: data,
    },
    useRowSelect,
    (hooks) => {
      let columnsConvert = [...columns];
      const externalBeforeColumns: any [] = [];
      if (columnsActions) {
        externalBeforeColumns.splice(1, 0, columnsActions)
        hooks.allColumns.push((columns) => [
          ...columnsConvert,
        ]);
      }
      if (selectionMode === SELECTION_MODE.MULTI) {
        externalBeforeColumns.push({
          id: "selection",
          Header: (row: any) => renderSelectionCell(row, "header", selectionMode),
          Cell: ({ row }: any) => renderSelectionCell({row}, "body", selectionMode),
        })
      }
      if (selectionMode === SELECTION_MODE.SINGLE) {
        externalBeforeColumns.push( {
          id: "selection",
          Header: () => renderSelectionCell({}, "header", selectionMode),
          Cell: ({ row }: any) => renderSelectionCell({row}, "body", selectionMode),
        })
      }
      hooks.allColumns.push((columns) => {
        return [
          ...externalBeforeColumns,
          ...columnsConvert,
        ]
      });
    }
  );

  const renderSelectionCell = (props: any, componentType: "header" | "body", type: string) => {
    const {row, getToggleAllRowsSelectedProps} = props;
    if (componentType === "header") {
      return (
        <th className="border-top d-flex align-items-center justify-content-center spaces h-35">
          {type === SELECTION_MODE.MULTI ? (
            <div className="spaces p-0 min-w-30px customs-form-check__table"/>
          ) : (
            <Form.Check
              type="checkbox"
              hidden={isHiddenCheckall}
              className="spaces p-0 min-w-30px customs-form-check__table"
              {...getToggleAllRowsSelectedProps?.()}
              onClick={toggleAllRowsSelected}
            />
          )}
        </th>
      )
    } else {
      return (
        <Form.Check
          type={type === SELECTION_MODE.SINGLE ? "radio" : "checkbox"}
          className={clsx(
            "my-0 mt-1",
            {"p-0 min-w-30px customs-form-check__table": type === SELECTION_MODE.MULTI},
            {"p-4 customs-form-check__radio__table": type === SELECTION_MODE.SINGLE},
          )}
          {...row.getToggleRowSelectedProps()}
        />
      )
    }
  }

  const toggleRowSelection = (row: any) => {
    row.toggleRowSelected();
    const isSelected = row?.isSelected;
    if (!isSelected) {
      setSelectedRows([...selectedRows, row]);
    } else {
      setSelectedRows(
        selectedRows.filter((selectedRow: Row<T>) => selectedRow.id !== row.id)
      );
    }
  };

  const toggleAllRowsSelected = () => {
    const allRowsSelected = rows.every((row: any) => row.isSelected);
    setSelectedRows(allRowsSelected ? [] : rows);
  };
  const handleUnSelectedRows = () => {
    rows.forEach((row: any) => {
      if (row.isSelected) {
        row.toggleRowSelected();
      }
    });
    setSelectedRows([]);
  };

  const handleSingleSelect = async (selectedRow: any) => {
    if (selectedRow.isSelected) {
      handleUnSelectedRows();
    } else {
      handleUnSelectedRows();
      await selectedRow.toggleRowSelected();
      setSelectedRows([selectedRow]);
    }
  };
  const handleCustomContextMenu = (e: any, row: any) => {
    e.preventDefault();
    handleContextMenu && handleContextMenu(e, row);
  };
  return (
    <KTCardBody height={height} className={className}>
      {hasToolbar && (
        <TableCustomToolbar
          selectedLabel={selectedLabel}
          handleUnSelectedRows={handleUnSelectedRows}
          selectedRows={selectedRows}
          allColumns={allColumns}
          setShouldOpenFilterSearch={setShouldOpenFilterSearch}
          shouldOpenFilterSearch={shouldOpenFilterSearch || false}
        />
      )}
      <div
        id={props.id || ""}
        style={{ ...styles, height: height }}
        className={clsx(
          "table-responsive table-scroll h-100",
          {"table-scroll-y": verticalScroll},
          className,
          height
        )}
        onScroll={onScroll}
      >
        <table
          id="kt_table_users"
          className="table align-middle table-row-dashed table-position fs-6 gy-5 dataTable no-footer"
          {...getTableProps()}
        >
          <thead
            className={
              verticalScroll
                ? "header-sticky z-index-1"
                : "position-sticky top-0 z-index-1"
            }
          >
          {headerGroups.map((headerGroup) => (
            <tr
              {...headerGroup.getHeaderGroupProps()}
              className="text-start border fw-bolder fs-8 gs-0 border bg-pri border-y-none "
            >
              {headerGroup.headers.map((column) => (
                <TableCustomHeaderColumn<T> key={column.id} column={column} />
              ))}
            </tr>
          ))}
          </thead>

          <tbody
            className="text-gray fw-bold bg-white border"
            {...getTableBodyProps()}
          >
          {rows.length > 0 ? (
            rows.map((row: any, i) => {
              prepareRow(row);
              return (
                <tr
                  className={clsx("cursor-pointer", {
                    "selected-row": row.isSelected
                  })}
                  {...row.getRowProps()}
                  onClick={() => {
                    if (selectionMode === SELECTION_MODE.MULTI) {
                      toggleRowSelection(row);
                    }
                    if (selectionMode === SELECTION_MODE.SINGLE) {
                      handleSingleSelect(row);
                    }
                    if (
                      selectionMode === SELECTION_MODE.SINGLE_NO_RADIO_BUTTON
                    ) {
                      handleSingleSelect(row);
                    }
                  }}
                  onDoubleClick={() => {
                    handleDoubleClick && handleDoubleClick(row);
                  }}
                  onContextMenu={(e) => handleCustomContextMenu(e, row)}
                >
                  {row.cells.map((cell: any, index: number) => {
                    return (
                      <td
                        className={"align-middle spaces pt-6 pb-2 pr-2 pl-2"}
                        {...cell.getCellProps()}
                      >
                        {cell.render("Cell") as ReactNode}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          ) : (
            <tr>
              <td
                colSpan={headerGroups[0].headers?.length}
                className="text-center py-2"
              >
                {intl.formatMessage({ id: "TABLE.DATA.EMPTY" })}
              </td>
            </tr>
          )}
          </tbody>
        </table>
      </div>
    </KTCardBody>
  );
}

export { TableCustom };
